import { useState } from "react";
import { format, differenceInDays, parseISO, isToday } from "date-fns";
import { ChevronDown, ChevronUp, FileText, Anchor, Plane, Box } from "lucide-react";
import TimelineView from "./TimelineView";

export default function ContainerTable({ containers, sort, sortBy, handleEdit, handleDelete, badge }) {
  const [expandedId, setExpandedId] = useState(null);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider text-xs border-b border-slate-200">
            <tr>
              <th className="px-6 py-4 w-10"></th>
              {["Ref", "Origin", "Destination", "ETA", "Cutoff", "Mode", "Type", "Status"].map((label, i) => (
                <th
                  key={i}
                  className="px-6 py-4 cursor-pointer hover:text-navy-900 transition-colors"
                  onClick={() => sortBy(label.toLowerCase())}
                >
                  <div className="flex items-center gap-1">
                    {label} {sort.key === label.toLowerCase() && (sort.direction === "asc" ? "↑" : "↓")}
                  </div>
                </th>
              ))}
              <th className="px-6 py-4 text-center">Docs</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {containers.map((c) => {
              const dueDays = c.cutoff ? differenceInDays(parseISO(c.cutoff), new Date()) : null;
              const isCutoffNear = dueDays !== null && dueDays <= 3 && dueDays >= 0;
              const isETAToday = c.eta && isToday(parseISO(c.eta));

              const rowClass = `transition-colors hover:bg-slate-50 ${expandedId === c.id ? 'bg-slate-50' : ''}`;

              return (
                <div key={c.id} style={{ display: "contents" }}>
                  <tr className={rowClass}>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleExpand(c.id)}
                        className="text-slate-400 hover:text-navy-900 transition-colors"
                      >
                        {expandedId === c.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    </td>
                    <td className="px-6 py-4 font-bold text-navy-900">{c.reference}</td>
                    <td className="px-6 py-4 text-slate-600">{c.origin}</td>
                    <td className="px-6 py-4 text-slate-600">{c.destination}</td>
                    <td className="px-6 py-4 ">
                      {c.eta ? (
                        <span className={isETAToday ? "text-emerald-600 font-bold" : "text-slate-600"}>
                          {format(parseISO(c.eta), "dd MMM")}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-6 py-4">
                      {c.cutoff ? (
                        <span className={isCutoffNear ? "text-red-500 font-bold" : "text-slate-600"}>
                          {format(parseISO(c.cutoff), "dd MMM")}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-6 py-4 text-slate-600 flex items-center gap-2">
                      {c.mode === 'Air' ? <Plane size={14} /> : <Anchor size={14} />}
                      {c.mode}
                    </td>
                    <td className="px-6 py-4 text-slate-600">{c.containerType}</td>
                    <td className="px-6 py-4">
                      <StatusBadge status={c.status} />
                    </td>
                    <td className="px-6 py-4 text-center">
                      {c.docs && Object.keys(c.docs).length > 0 ? (
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-50 text-blue-600" title="Has Documents">
                          <FileText size={14} />
                        </span>
                      ) : (
                        <span className="text-slate-300">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right space-x-3">
                      <button onClick={() => handleEdit(c)} className="text-sm font-medium text-navy-700 hover:text-gold-600">Edit</button>
                      <button onClick={() => handleDelete(c.id)} className="text-sm font-medium text-red-400 hover:text-red-600">Delete</button>
                    </td>
                  </tr>

                  {expandedId === c.id && (
                    <tr>
                      <td colSpan="11" className="p-0">
                        <TimelineView container={c} />
                      </td>
                    </tr>
                  )}
                </div>
              );
            })}
            {containers.length === 0 && (
              <tr>
                <td colSpan="11" className="px-6 py-12 text-center text-slate-400 italic">
                  No shipments found matching your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const s = (status || "").toLowerCase();

  let classes = "bg-slate-100 text-slate-600 border-slate-200";

  // Delivered
  if (s === "delivered") classes = "bg-emerald-50 text-emerald-700 border-emerald-200";
  // In Transit / Dispatched
  if (["in transit", "dispatched", "on board"].includes(s)) classes = "bg-blue-50 text-blue-700 border-blue-200";
  // Pending / Booked
  if (["booked", "wip"].includes(s)) classes = "bg-slate-100 text-slate-600 border-slate-200";
  // Warning
  if (["delayed", "hold"].includes(s)) classes = "bg-amber-50 text-amber-700 border-amber-200";

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${classes} capitalize`}>
      {status || "Pending"}
    </span>
  );
}